import { Report, Customer, Agent, ReportMachine, ReportTimeLog, ReportSparePart, TravelLeg } from '../types';
import { safeFormatDate } from './dateUtils';
import { minutesToTimeString } from './timeUtils';

// ============================================
// Ubaldo DTO types (matching report.hbs template)
// ============================================

export interface UbaldoCustomerDTO {
  name: string;
  country: string;
  address: string;
  telephone: string;
  city: string;
  email: string;
  province: string;
  finalCustomer: string;
}

export interface UbaldoTechnicianDTO {
  name: string;
  surname: string;
}

export interface UbaldoDatesDTO {
  start: string;
  finish: string;
  workTime: string;
  travelTime: string;
  km: string;
}

export interface UbaldoTimeLogDTO {
  date: string;
  start: string;
  finish: string;
  breakTime: string;
  travelTime: string;
  travelKm: string;
}

export interface UbaldoSparePartDTO {
  partNumber: string;
  description: string;
  shipped: number;
  used: number;
  warranty: string;
}

export interface UbaldoMachineDTO {
  serialNumber: string;
  idMachine: string;
  description: string;
  type: string;
  issueNotified: string;
  customerReferencePerson: string;
  swVer: string;
  osVer: string;
  workPerformed: string;
  recommendationForFuture: string;
  followUpAfterServiceVisit: string;
  timeLogs: UbaldoTimeLogDTO[];
  spareParts: UbaldoSparePartDTO[];
}

export interface UbaldoFooterDTO {
  date: string;
}

export interface UbaldoTravelLegDTO {
  date: string;
  departureTime: string;
  arrivalTime: string;
  nextDay: boolean;
  breakTime: string;
  km: string;
}

export interface UbaldoTravelSectionDTO {
  outbound: UbaldoTravelLegDTO | null;
  inbound: UbaldoTravelLegDTO | null;
}

export interface UbaldoServiceLocationDTO {
  description: string;
  address: string;
  city: string;
  country: string;
}

export interface UbaldoReportDTO {
  reportNumber: string;
  reportDate: string;
  logo: string;
  customerSignature: string;
  technicianSignature: string;
  customer: UbaldoCustomerDTO;
  technicians: UbaldoTechnicianDTO[];
  dates: UbaldoDatesDTO;
  footer: UbaldoFooterDTO;
  machines: UbaldoMachineDTO[];
  travelSection: UbaldoTravelSectionDTO;
  serviceLocation: UbaldoServiceLocationDTO | null;
}

// ============================================
// Mapping helpers
// ============================================

const DATE_FORMAT = 'dd/MM/yyyy';
const TIME_FORMAT = 'HH:mm';

function formatDate(isoString: string | undefined): string {
  return safeFormatDate(isoString, DATE_FORMAT, '');
}

function formatTime(isoString: string | undefined): string {
  return safeFormatDate(isoString, TIME_FORMAT, '');
}

/**
 * Splits a full name string into first name and surname.
 * The last space-separated token is treated as surname.
 */
function splitTechnicianName(fullName: string): { name: string; surname: string } {
  const trimmed = fullName.trim();
  const spaceIndex = trimmed.lastIndexOf(' ');
  if (spaceIndex === -1) {
    return { name: trimmed, surname: '' };
  }
  return {
    name: trimmed.substring(0, spaceIndex),
    surname: trimmed.substring(spaceIndex + 1),
  };
}

function mapTimeLog(log: ReportTimeLog): UbaldoTimeLogDTO {
  return {
    date: formatDate(log.logDate),
    start: formatTime(log.startTime),
    finish: formatTime(log.finishTime),
    breakTime: log.breakTime != null ? minutesToTimeString(log.breakTime) : '',
    travelTime: log.travelTime != null ? minutesToTimeString(log.travelTime) : '',
    travelKm: log.travelKm != null ? String(log.travelKm) : '',
  };
}

function mapSparePart(sp: ReportSparePart): UbaldoSparePartDTO {
  return {
    partNumber: sp.partNumber ?? '',
    description: sp.descriptionEn || sp.descriptionIt || '',
    shipped: sp.quantity ?? 0,
    used: sp.used ?? 0,
    warranty: sp.warranty ? 'X' : '',
  };
}

function mapMachine(machine: ReportMachine): UbaldoMachineDTO {
  return {
    serialNumber: machine.idNumber ?? '',
    idMachine: machine.idMachine ?? '',
    description: machine.description ?? '',
    type: machine.serviceType ?? '',
    issueNotified: machine.problemDescription ?? '',
    customerReferencePerson: machine.customerReference ?? '',
    swVer: machine.softwareVersion ?? '',
    osVer: machine.osVersion ?? '',
    workPerformed: machine.workDescription ?? '',
    recommendationForFuture: machine.futureWorksDescription ?? '',
    followUpAfterServiceVisit: machine.followUpAfterService ?? '',
    timeLogs: (machine.timeLogs ?? []).map(mapTimeLog),
    spareParts: (machine.spareParts ?? []).map(mapSparePart),
  };
}

function mapTravelLeg(leg: TravelLeg | undefined): UbaldoTravelLegDTO | null {
  if (!leg) return null;
  return {
    date: leg.date || '',
    departureTime: leg.departureTime || '',
    arrivalTime: leg.arrivalTime || '',
    nextDay: leg.nextDay || false,
    breakTime: leg.breakTime != null ? String(leg.breakTime) : '',
    km: leg.km != null ? String(leg.km) : '',
  };
}

// ============================================
// Main mapper
// ============================================

/**
 * Maps Jannik domain objects to the Ubaldo report generation DTO.
 *
 * @param report   - Full report object (including machines with timeLogs and spareParts)
 * @param customer - Customer details fetched from the API
 * @param agent    - Agent (logo owner) fetched from the API; pass null if not available
 * @param customerSignatureDate - updatedAt of the latest customer signature; the footer date falls back to
 *                                today when the report has not been signed by the customer yet
 */
export function mapReportToUbaldoDTO(
  report: Report,
  customer: Customer | null | undefined,
  agent: Agent | null | undefined,
  calculatedWorkTime?: number,
  calculatedTravelTime?: number,
  calculatedTravelKm?: number,
  customerSignatureContent?: string,
  technicianSignatureContent?: string,
  customerSignatureDate?: string
): UbaldoReportDTO {
  const technicians: UbaldoTechnicianDTO[] = [
    splitTechnicianName(report.technicianName ?? ''),
    ...(report.supportTechnicianName ? [splitTechnicianName(report.supportTechnicianName)] : []),
  ];

  const signatureDate = formatDate(customerSignatureDate) || formatDate(new Date().toISOString());

  const workTime = calculatedWorkTime != null
    ? minutesToTimeString(calculatedWorkTime)
    : (report.workTime != null ? minutesToTimeString(report.workTime) : '');

  const travelTime = calculatedTravelTime != null
    ? minutesToTimeString(calculatedTravelTime)
    : (report.travelTime != null ? minutesToTimeString(report.travelTime) : '');

  return {
    reportNumber: report.number ?? '',
    reportDate: formatDate(report.creation),
    logo: agent?.imageBase64 ?? '',
    customerSignature: customerSignatureContent ?? '',
    technicianSignature: technicianSignatureContent ?? '',

    customer: {
      name: customer?.name ?? report.customerName ?? '',
      country: customer?.countryCode ?? '',
      address: customer?.address ?? '',
      telephone: customer?.phone ?? '',
      city: customer?.city ?? '',
      email: customer?.email ?? '',
      province: '',
      finalCustomer: '',
    },

    technicians,

    dates: {
      start: formatDate(report.startTime),
      finish: formatDate(report.finishTime),
      workTime,
      travelTime,
      km: calculatedTravelKm != null ? String(calculatedTravelKm) : (report.travelKm != null ? String(report.travelKm) : ''),
    },

    footer: {
      date: signatureDate,
    },

    machines: (report.machines ?? []).map(mapMachine),

    travelSection: {
      outbound: mapTravelLeg(report.travelTimeData?.outbound),
      inbound: mapTravelLeg(report.travelTimeData?.inbound),
    },

    serviceLocation: report.serviceLocation && (
      report.serviceLocation.description ||
      report.serviceLocation.address ||
      report.serviceLocation.city ||
      report.serviceLocation.country
    ) ? {
      description: report.serviceLocation.description ?? '',
      address: report.serviceLocation.address ?? '',
      city: report.serviceLocation.city ?? '',
      country: report.serviceLocation.country ?? '',
    } : null,
  };
}
